import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { detectLocation } from '../utils/location';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '../lib/axios';
import { useTranslation } from 'react-i18next';

const API_BASE = api.defaults.baseURL || '/api';

// Plain fetch with timeout — 30s to accommodate slow Railway server (Singapore edge ~7s connect)
const fetchWithTimeout = async (url: string, timeoutMs = 30000) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        const res = await fetch(url, {
            signal: controller.signal,
            headers: { 'Accept': 'application/json' }
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return await res.json();
    } finally {
        clearTimeout(timer);
    }
};

interface Advertisement {
    id: string;
    advertiser: string;
    imageUrl: string;
    targetUrl?: string;
    type: string;
    clicks?: number;
}

export type AdLayout = 'SIDEBAR_TALL' | 'BANNER_WIDE' | 'COMPACT';

interface AdBannerProps {
    position: string;
    className?: string;
    country?: string;
    state?: string;
    city?: string;
    layout?: AdLayout;
}

const adsCache: Record<string, Advertisement[]> = {};
const indexCache: Record<string, number> = {};

export const AdBanner: React.FC<AdBannerProps> = ({ 
    position, 
    className, 
    country, 
    state, 
    city,
    layout = 'BANNER_WIDE' 
}) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const cacheKey = React.useMemo(() => 
        `${position}-${user?.id || 'guest'}-${country || 'detect'}-${state || ''}-${city || ''}-${layout}`,
    [position, user?.id, country, state, city, layout]);

    const [ads, setAds] = useState<Advertisement[]>(adsCache[cacheKey] || []);
    const [currentAdIndex, setCurrentAdIndex] = useState(indexCache[cacheKey] || 0);
    const [isLoading, setIsLoading] = useState(ads.length === 0);

    useEffect(() => {
        const fetchAds = async () => {
            try {
                let geo = null;
                if (!country && !state && !city) {
                    geo = await detectLocation();
                } else {
                    geo = { country, state, city };
                }

                let url = `${API_BASE}/promotions/active/${position}`;
                const params = new URLSearchParams();
                if (geo) {
                    if (geo.country) params.append('country', geo.country);
                    if (geo.state) params.append('state', geo.state);
                    if (geo.city) params.append('city', geo.city);
                }
                if (params.toString()) {
                    url += `?${params.toString()}`;
                }

                const data = await fetchWithTimeout(url);
                adsCache[cacheKey] = data;
                
                if (currentAdIndex >= data.length) {
                    setCurrentAdIndex(0);
                    indexCache[cacheKey] = 0;
                }
                
                setAds(data);
            } catch (error) {
                console.error('Failed to load advertisements', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAds();

        const handleRefresh = () => {
            delete adsCache[cacheKey]; 
            fetchAds();
        };
        window.addEventListener('ads-updated', handleRefresh);
        return () => window.removeEventListener('ads-updated', handleRefresh);
    }, [cacheKey]);

    useEffect(() => {
        if (ads.length > 1) {
            const interval = setInterval(() => {
                setCurrentAdIndex(prev => {
                    const next = (prev + 1) % ads.length;
                    indexCache[cacheKey] = next;
                    return next;
                });
            }, 8000);
            return () => clearInterval(interval);
        }
    }, [ads.length, cacheKey]);

    const ad = ads[currentAdIndex] || ads[0];

    const [trackedImpressions, setTrackedImpressions] = useState<Set<string>>(new Set());
    const adRef = React.useRef<HTMLAnchorElement>(null);

    useEffect(() => {
        if (!ad?.id || trackedImpressions.has(ad.id)) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    fetch(`${API_BASE}/promotions/${ad.id}/impression`, { method: 'PATCH', credentials: 'include' }).catch(() => {});
                    setTrackedImpressions(prev => new Set(prev).add(ad.id));
                    observer.disconnect();
                }
            },
            { threshold: 0.5 }
        );

        if (adRef.current) observer.observe(adRef.current);
        return () => observer.disconnect();
    }, [ad?.id, trackedImpressions]);

    // Layout-specific styling
    const getLayoutClasses = () => {
        switch (layout) {
            case 'SIDEBAR_TALL':
                return 'aspect-[16/9] md:aspect-[4/5] h-auto w-full';
            case 'COMPACT':
                return 'h-24 md:h-28 w-full';
            case 'BANNER_WIDE':
            default:
                return position === 'DASHBOARD' ? 'h-40 md:h-52 w-full' : 'h-32 w-full';
        }
    };

    const isYouTube = (url: string) => url && /youtube\.com|youtu\.be/.test(url);
    const isVideo = (url: string) => url && /\.(mp4|webm|ogg)$/i.test(url);
    
    const getYoutubeId = (url: string) => {
        if (!url) return null;
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url.match(regExp);
        return (match && match[2].length === 11) ? match[2] : null;
    };

    const renderMedia = (url: string, alt: string, isBackground = false) => {
        if (!url) return null;
        
        if (isYouTube(url)) {
            const videoId = getYoutubeId(url);
            if (!videoId) return <img src={url} alt={alt} className="w-full h-full object-cover" />;
            return (
                <div className={`w-full h-full ${isBackground ? 'blur-2xl opacity-30 scale-150' : ''}`}>
                    <iframe
                        src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=0&loop=1&playlist=${videoId}&modestbranding=1&rel=0`}
                        title={alt}
                        className="w-full h-full border-none pointer-events-none"
                        allow="autoplay; encrypted-media"
                    />
                </div>
            );
        }

        if (isVideo(url)) {
            return (
                <video
                    src={url}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className={`w-full h-full object-cover ${isBackground ? 'blur-2xl opacity-30 scale-150' : ''}`}
                />
            );
        }

        return (
            <img
                src={url}
                alt={alt}
                className={`w-full h-full object-cover transition-all duration-[3000ms] ${
                    !isBackground ? 'group-hover:scale-110' : 'blur-2xl opacity-30 scale-150 group-hover:scale-[1.6]'
                }`}
                onError={(e) => {
                    if (!isBackground) (e.target as HTMLImageElement).style.visibility = 'hidden';
                }}
            />
        );
    };

    const handleAdClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        if (!ad?.id) return;

        // Fire click-tracking in background — don't wait
        fetch(`${API_BASE}/promotions/${ad.id}/click`, {
            method: 'PATCH',
            credentials: 'include',
        }).catch(() => {});

        // Navigate the user to the advertiser's actual target URL
        if (ad.targetUrl) {
            let url = ad.targetUrl.trim();
            if (!/^https?:\/\//i.test(url)) {
                url = `https://${url}`;
            }
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };

    const glassClasses = "bg-white/40 backdrop-blur-md border border-white/50 shadow-[0_8px_32px_rgba(31,38,135,0.15)] ring-1 ring-white/20";
    const finalClassName = `relative overflow-hidden rounded-[2rem] transition-all duration-500 group ${getLayoutClasses()} ${className || ''} ${glassClasses}`;

    if (ads.length === 0 || !ad?.id) {
        return (
            <div className={finalClassName}>
                <div className="absolute inset-0 bg-gradient-to-br from-white/60 to-brand-50/20" />
                <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
                    {isLoading ? (
                        <div className="flex flex-col items-center gap-3">
                            <div className="w-6 h-6 border-2 border-brand-500/20 border-t-brand-500 rounded-full animate-spin" />
                            <span className="text-[10px] font-black text-brand-600/40 uppercase tracking-[0.2em]">{t('ads.curating', 'Curating...')}</span>
                        </div>
                    ) : (
                        <>
                            <div className="p-2 bg-white/50 rounded-xl mb-2 backdrop-blur-sm">
                                <p className="text-brand-900 text-[10px] font-black tracking-tight uppercase">{t('ads.premiumPartner', 'Premium Partner')}</p>
                            </div>
                            <p className="text-slate-400 text-[8px] uppercase font-black tracking-widest">{t('ads.networkActive', 'Network Active')}</p>
                        </>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className={finalClassName}>
            <AnimatePresence mode="wait">
                <motion.a
                    key={ad.id}
                    ref={adRef}
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.02 }}
                    transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
                    href={ad.targetUrl || '#'}
                    onClick={handleAdClick}
                    rel="noopener noreferrer"
                    className="absolute inset-0 block overflow-hidden cursor-pointer"
                >
                    {/* Background Glow */}
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                        {renderMedia(ad.imageUrl, "", true)}
                    </div>

                    {/* Main Content */}
                    <div className="relative z-10 w-full h-full p-2">
                        <div className="w-full h-full rounded-[1.5rem] overflow-hidden shadow-inner bg-slate-50/50">
                            {renderMedia(ad.imageUrl, ad.advertiser)}
                        </div>
                    </div>

                    {/* Overlay Info */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10 pointer-events-none" />
                    
                    <div className="absolute bottom-5 left-6 z-20 transition-transform duration-500 group-hover:translate-x-1">
                        <div className="flex flex-col">
                            <span className="text-white/60 text-[8px] font-black uppercase tracking-[0.2em] mb-1">{t('ads.partnerShowcase', 'Partner Showcase')}</span>
                            <p className="text-white text-base font-black tracking-tight uppercase leading-none drop-shadow-sm">{ad.advertiser}</p>
                        </div>
                    </div>

                    {/* Premium Badge */}
                    <div className="absolute top-5 right-6 z-20">
                        <div className="flex items-center gap-1.5 px-3 py-1 bg-white/90 backdrop-blur-md rounded-full border border-white shadow-lg transition-all duration-300 group-hover:scale-105 group-hover:-rotate-2">
                            <div className="w-1 h-1 bg-brand-500 rounded-full animate-ping" />
                            <span className="text-brand-900 text-[8px] font-black uppercase tracking-[0.2em]">{t('ads.sponsored', 'Sponsored')}</span>
                        </div>
                    </div>
                </motion.a>
            </AnimatePresence>
        </div>
    );
};
